'use client'

import { Badge, Space, Table, type TableProps, Typography } from 'antd'
import useSWR from 'swr'
import { useTranslation } from 'react-i18next'
import Link from 'next/link'
import { fetchDeploymentList } from '@/service/deployment'
import Status from '@/app/components/mlops/Status'
import Datetime from '@/app/components/mlops/Datetime'
import PermissionTag from '@/app/components/mlops/PermissionTag'
import ImageLink from '@/app/components/mlops/ImageLink'
import ResourceSelectorDesc from '@/app/components/mlops/ResourceSelectorDesc'
import ModelLink from '@/app/components/mlops/ModelLink'
import TruncatedId from '@/app/components/mlops/TruncatedId'

const DeploymentDisplayList = () => {
  const { t } = useTranslation()

  const { data, isLoading } = useSWR(
    {
      url: '/api/deployments',
      params: {
        page: 1,
        limit: 5, // 只获取5条数据
        search: '',
      },
    },
    fetchDeploymentList,
    {
      refreshInterval: 1000,
    },
  )

  const columns: TableProps['columns'] = [
    {
      title: <span className='whitespace-nowrap'>{t('deployment.idAndName')}</span>,
      dataIndex: 'id',
      width: 220,
      fixed: 'left',
      render: (text, record) => (
        <Space size={0} direction='vertical'>
          <Link
            className='text-[#1677ff] hover:text-[#69b1ff]'
            href={`/deploy/deployments/${text}${record.status === 'init' ? '/edit' : ''}`}>
            <Typography.Link>{record.name || '--'}</Typography.Link>
          </Link>
          <TruncatedId id={text} />
        </Space>
      ),
    },
    {
      title: <span className='whitespace-nowrap'>{t('deployment.status')}</span>,
      dataIndex: 'status',
      width: 120,
      render: text => <Status value={text} />,
    },
    {
      title: <span className='whitespace-nowrap'>{t('deployment.permission')}</span>,
      dataIndex: 'permission',
      width: 100,
      render: text => <PermissionTag value={text} />,
    },
    {
      title: <span className='whitespace-nowrap'>{t('deployment.model')}</span>,
      dataIndex: 'llm_id',
      width: 200,
      render: (text, record) => <ModelLink model={record.llm} />,
    },
    {
      title: <span className='whitespace-nowrap'>{t('deployment.image')}</span>,
      dataIndex: 'image_id',
      width: 220,
      render: (text, { image }) => <ImageLink image={image} />,
    },
    {
      title: <span className='whitespace-nowrap'>{t('deployment.resourceInfo')}</span>,
      dataIndex: 'resource_selector',
      width: 200,
      render: (text, record) => (
        <ResourceSelectorDesc resource_selector={record.resource_selector} />
      ),
    },
    {
      title: <span className='whitespace-nowrap'>{t('deployment.replicas')}</span>,
      dataIndex: 'replicas',
      width: 100,
      render: (text, record) => (
        <Badge
          color='green'
          showZero
          count={record.resource_selector?.replicas || 0}
        />
      ),
    },
    {
      title: <span className='whitespace-nowrap'>{t('deployment.createdAt')}</span>,
      dataIndex: 'created_at',
      width: 180,
      render: (text: any, record) => <Datetime value={record.created_at} />,
    },
    {
      title: <span className='whitespace-nowrap'>{t('deployment.updatedAt')}</span>,
      dataIndex: 'updated_at',
      width: 180,
      render: (text: any, record) => <Datetime value={record.updated_at} />,
    },
  ]

  return (
    <Table
      loading={isLoading}
      className='pt-2'
      columns={columns}
      dataSource={data?.data}
      rowKey='id'
      scroll={{ x: 1400 }}
      locale={{
        emptyText: t('deployment.list.empty_text') as string,
      }}
      pagination={false} // 不显示分页
    />
  )
}

export default DeploymentDisplayList
